import { isServer } from 'tamagui';
import { useFF4JStore } from '../store/useFF4JStore';
import { IEvaluateFeature, IProjectDetails, IRequestEvaluateFeature } from '../types/featureFlags';

class FF4JClient {
  baseUrl: string;
  apiKey: string;
  pollingInterval: number;
  cache: Map<string, IEvaluateFeature>;
  storageKey: string;
  pollingTimer: NodeJS.Timeout | null;
  projectDetails: IProjectDetails;

  constructor(
    baseUrl: string,
    apiKey: string,
    projectDetails: IProjectDetails,
    pollingInterval = 60000
  ) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
    this.pollingInterval = pollingInterval;
    this.cache = new Map();
    this.storageKey = 'ff4j-feature-flags';
    this.pollingTimer = null;
    this.projectDetails = projectDetails;
    if (!isServer) this.loadFromStorage();
  }

  async fetchFeatureFlag(
    featureName: string,
    contextName: string,
    contextParameter: string
  ): Promise<IEvaluateFeature> {
    try {
      const requestBody: IRequestEvaluateFeature = {
        ...this.projectDetails,
        featureName,
        contextName,
        contextParameter,
      };

      const response = await fetch(
        `${this.baseUrl}/feature-toggle/api/v1/realms/1/devices/${contextParameter}/evaluate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-nc-tool-id': this.projectDetails.projectToolId,
            // Authorization: `Bearer ${this.apiKey}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();
      return {
        ...data,
        contextName: contextName,
        contextParameter: contextParameter,
      } as IEvaluateFeature;
    } catch (error) {
      console.error('FF4J fetch error:', error);
    }

    return {
      featureName: featureName,
      featureEnabled: false,
      treatmentDetails: {
        treatmentName: 'OFF',
        treatmentId: 0,
        configuration: {},
        rolloutPercentage: 0,
        segmentIds: null,
        defaultTreatment: true,
      },
      contextName: contextName,
      contextParameter: contextParameter,
    };
  }

  async isFeatureEnabled(
    feature: string,
    contextName: string,
    contextParameter: string
  ): Promise<IEvaluateFeature> {
    try {
      const store = useFF4JStore.getState();

      if (feature in store.flags && store.flags[feature].featureName !== undefined) {
        return store.flags[feature];
      }

      const enabled = await this.fetchFeatureFlag(feature, contextName, contextParameter);
      this.cache.set(feature, enabled);
      useFF4JStore.getState().setFlag(feature, enabled);
      this.saveToStorage();
      return enabled;
    } catch (error) {
      console.error('Error evaluating feature treatment => ', error);
    }

    return {
      featureName: feature,
      featureEnabled: false,
      treatmentDetails: {
        treatmentName: 'OFF',
        treatmentId: 0,
        configuration: {},
        rolloutPercentage: 0,
        segmentIds: null,
        defaultTreatment: true,
      },
      contextName: contextName,
      contextParameter: contextParameter,
    };
  }

  saveToStorage() {
    if (!isServer) {
      try {
        const obj = Object.fromEntries(this.cache);
        useFF4JStore.getState().setAllFlags(obj);
      } catch (e) {
        console.error('Failed to save flags to MMKV:', e);
      }
    }
  }

  async loadFromStorage() {
    if (!isServer) {
      try {
        const storedFlags = useFF4JStore.getState().flags;
        if (storedFlags) {
          this.cache = new Map(Object.entries(storedFlags));
          useFF4JStore.getState().setAllFlags(storedFlags);
        }
      } catch (error) {
        console.error('Error loading flags from MMKV:', error);
      }
    }
  }

  startPolling() {
    if (this.pollingTimer) clearInterval(this.pollingTimer);
    this.pollingTimer = setInterval(async () => {
      for (const feature of this.cache.keys()) {
        const { contextName, contextParameter } = this.cache.get(feature) || {};
        if (!contextName || !contextParameter) continue;
        const enabled = await this.fetchFeatureFlag(feature, contextName, contextParameter);
        if (enabled) {
          this.cache.set(feature, enabled);
          useFF4JStore.getState().setFlag(feature, enabled);
        }
      }
      this.saveToStorage();
    }, this.pollingInterval);
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
}

export default FF4JClient;
